/**
 * Title-and-abstract screening: loading records, recording decisions, and exporting them.
 *
 * Records can come from RIS, CSV, PubMed .nbib or EndNote XML. A CSV written by this tool
 * carries the decisions, reasons and notes in its first columns, so loading it again
 * resumes screening where it stopped. Nothing is stored by the page: the exported file is
 * the save.
 */

import { convertEndnoteXml } from "@/lib/tool-kit/research-discovery/endnote";
import { convertNbib } from "@/lib/tool-kit/research-discovery/nbib";
import {
  emptyRecord,
  nameFromText,
  type RefName,
  type RefRecord,
} from "@/lib/tool-kit/research-discovery/records";
import { risToRecords } from "@/lib/tool-kit/research-discovery/ris-read";
import {
  detectDelimiter,
  parseDelimited,
  toDelimited,
} from "@/lib/tool-kit/text/delimited";

export type Decision = "include" | "exclude" | "maybe";

export interface Verdict {
  decision: Decision;
  reason: string;
  note: string;
}

export type Verdicts = Readonly<Record<number, Verdict>>;

export type SourceFormat = "ris" | "csv" | "nbib" | "endnote";

export interface LoadResult {
  records: RefRecord[];
  verdicts: Verdicts;
  format: SourceFormat | null;
  /** Why nothing loaded: "empty", "unrecognised", "noTitleColumn" or "noRecords". */
  problem?: string;
}

/* ------------------------------------------------------------------ loading */

const RE_RIS = /^\s*TY\s+-/m;
const RE_NBIB = /^\s*PMID-\s/m;
const RE_XML = /^\s*(?:<\?xml|<xml|<records|<record[\s>])/i;
const RE_YEAR = /\b(1[5-9]\d\d|20\d\d)\b/;
const RE_TRAILING_INITIALS = /^(.+?)\s+((?:[A-Z]\.?\s?){1,4})$/;
const RE_INITIAL_JUNK = /[.\s]/g;
const RE_KEYWORD_SPLIT = /[;|]/;
const RE_AND = /\s+and\s+/;
const RE_FORMULA_GUARD = /^'(?=[=+@\t\r])/;
const RE_FORMULA = /^[=+@\t\r]/;

export function detectFormat(text: string): SourceFormat | null {
  if (RE_XML.test(text)) {
    return "endnote";
  }
  if (RE_NBIB.test(text)) {
    return "nbib";
  }
  if (RE_RIS.test(text)) {
    return "ris";
  }
  return text.includes(",") || text.includes("\t") || text.includes(";")
    ? "csv"
    : null;
}

const COLUMNS: Record<string, readonly string[]> = {
  decision: ["decision", "screening decision"],
  reason: ["reason", "exclusion reason", "reason for exclusion"],
  note: ["note", "notes", "screening note"],
  title: ["title", "article title", "document title", "ti", "primary title"],
  abstract: ["abstract", "ab", "abstract note", "description"],
  authors: [
    "authors",
    "author",
    "author names",
    "author full names",
    "au",
    "creators",
  ],
  year: [
    "year",
    "publication year",
    "py",
    "pub year",
    "date",
    "publication date",
  ],
  journal: [
    "journal",
    "source title",
    "source",
    "publication title",
    "journal title",
    "container-title",
    "secondary title",
  ],
  doi: ["doi", "di"],
  keywords: ["keywords", "author keywords", "kw", "manual tags", "tags"],
  url: ["url", "link"],
};

type ColumnMap = Partial<Record<keyof typeof COLUMNS, number>>;

function mapColumns(header: readonly string[]): ColumnMap {
  const names = header.map((cell) => cell.trim().toLowerCase());
  const map: ColumnMap = {};
  for (const [key, synonyms] of Object.entries(COLUMNS)) {
    const at = names.findIndex((name) => synonyms.includes(name));
    if (at >= 0) {
      map[key] = at;
    }
  }
  return map;
}

/** Undo the guard `csvCell` puts before a leading = + @ so the value reads back as written. */
function unguard(value: string): string {
  return value.replace(RE_FORMULA_GUARD, "");
}

/** "Smith, Jane", "Jane Smith" and the database form "Smith J." / "Smith JA". */
export function looseName(raw: string): RefName {
  const initials = RE_TRAILING_INITIALS.exec(raw.trim());
  if (initials && !raw.includes(",")) {
    const given = [...initials[2].replace(RE_INITIAL_JUNK, "")]
      .map((letter) => `${letter}.`)
      .join(" ");
    return { family: initials[1], given };
  }
  return nameFromText(raw);
}

function splitAuthors(raw: string): string[] {
  const text = raw.trim();
  if (text === "") {
    return [];
  }
  if (text.includes(";")) {
    return text
      .split(";")
      .map((name) => name.trim())
      .filter(Boolean);
  }
  return RE_AND.test(text) ? text.split(RE_AND) : [text];
}

function splitKeywords(raw: string): string[] {
  return raw
    .split(RE_KEYWORD_SPLIT)
    .map((keyword) => keyword.trim())
    .filter(Boolean);
}

const DECISIONS: Record<string, Decision> = {
  include: "include",
  included: "include",
  yes: "include",
  i: "include",
  exclude: "exclude",
  excluded: "exclude",
  no: "exclude",
  e: "exclude",
  maybe: "maybe",
  unsure: "maybe",
  m: "maybe",
};

export function readDecision(value: string): Decision | null {
  return DECISIONS[value.trim().toLowerCase()] ?? null;
}

function csvRecord(row: readonly string[], columns: ColumnMap): RefRecord {
  const cell = (key: string): string =>
    unguard(
      (columns[key] === undefined ? "" : (row[columns[key]] ?? "")).trim()
    );
  const record = emptyRecord("GEN");
  record.title = cell("title");
  record.abstract = cell("abstract");
  record.authors = splitAuthors(cell("authors")).map(looseName);
  record.year = RE_YEAR.exec(cell("year"))?.[1] ?? "";
  record.container = cell("journal");
  record.doi = cell("doi").toLowerCase();
  record.keywords = splitKeywords(cell("keywords"));
  record.url = cell("url");
  return record;
}

function loadCsv(text: string): LoadResult {
  const { rows } = parseDelimited(text, detectDelimiter(text));
  const [header, ...body] = rows;
  const columns = mapColumns(header ?? []);
  if (columns.title === undefined) {
    return {
      records: [],
      verdicts: {},
      format: "csv",
      problem: "noTitleColumn",
    };
  }
  const records: RefRecord[] = [];
  const verdicts: Record<number, Verdict> = {};
  for (const row of body) {
    const record = csvRecord(row, columns);
    if (record.title === "" && record.abstract === "") {
      continue;
    }
    const decision = readDecision(
      columns.decision === undefined ? "" : (row[columns.decision] ?? "")
    );
    if (decision) {
      verdicts[records.length] = {
        decision,
        reason: unguard(
          (columns.reason === undefined
            ? ""
            : (row[columns.reason] ?? "")
          ).trim()
        ),
        note: unguard(
          (columns.note === undefined ? "" : (row[columns.note] ?? "")).trim()
        ),
      };
    }
    records.push(record);
  }
  return { records, verdicts, format: "csv" };
}

/** Read records (and, from a CSV this tool wrote, earlier decisions) from pasted or uploaded text. */
export function loadRecords(text: string): LoadResult {
  if (text.trim() === "") {
    return { records: [], verdicts: {}, format: null, problem: "empty" };
  }
  const format = detectFormat(text);
  if (format === null) {
    return { records: [], verdicts: {}, format: null, problem: "unrecognised" };
  }
  if (format === "csv") {
    return loadCsv(text);
  }
  let records: RefRecord[];
  if (format === "ris") {
    records = risToRecords(text);
  } else if (format === "nbib") {
    records = convertNbib(text).records;
  } else {
    records = convertEndnoteXml(text).records;
  }
  return records.length === 0
    ? { records, verdicts: {}, format, problem: "noRecords" }
    : { records, verdicts: {}, format };
}

/* ------------------------------------------------------------- duplicates */

const RE_NON_ALNUM = /[^\p{L}\p{N}]+/gu;

function titleKey(record: RefRecord): string {
  return `${record.title.toLowerCase().replace(RE_NON_ALNUM, "")}|${record.year}`;
}

/**
 * For each record that repeats an earlier one — same DOI, or same title and year — the
 * index of the first occurrence. Records with no DOI and an empty title never match.
 */
export function findDuplicates(
  records: readonly RefRecord[]
): ReadonlyMap<number, number> {
  const byDoi = new Map<string, number>();
  const byTitle = new Map<string, number>();
  const duplicates = new Map<number, number>();
  for (const [index, record] of records.entries()) {
    const title = record.title === "" ? "" : titleKey(record);
    const seen =
      (record.doi === "" ? undefined : byDoi.get(record.doi)) ??
      (title === "" ? undefined : byTitle.get(title));
    if (seen === undefined) {
      if (record.doi !== "") {
        byDoi.set(record.doi, index);
      }
      if (title !== "") {
        byTitle.set(title, index);
      }
    } else {
      duplicates.set(index, seen);
    }
  }
  return duplicates;
}

/* ---------------------------------------------------------------- terms */

export type HighlightKind = "include" | "exclude";

export interface Segment {
  text: string;
  kind: HighlightKind | null;
}

const RE_TERM_SPLIT = /[,\n]/;
const RE_ESCAPE = /[.*+?^${}()|[\]\\]/g;
export const MAX_TERMS = 50;

/** Terms from a comma- or line-separated list: trimmed, at least two characters, unique. */
export function parseTerms(raw: string): string[] {
  const seen = new Set<string>();
  const terms: string[] = [];
  for (const piece of raw.split(RE_TERM_SPLIT)) {
    const term = piece.trim();
    if (term.length >= 2 && !seen.has(term.toLowerCase())) {
      seen.add(term.toLowerCase());
      terms.push(term);
    }
  }
  return terms.slice(0, MAX_TERMS);
}

/**
 * Split `text` into plain and highlighted pieces. Matching is by letters inside words
 * ("random" marks "randomised"), ignores case, and prefers the longer term; where a word
 * is on both lists the exclude list wins, so a conflict is visible.
 */
export function highlight(
  text: string,
  include: readonly string[],
  exclude: readonly string[]
): Segment[] {
  const kinds = new Map<string, HighlightKind>();
  for (const term of include) {
    kinds.set(term.toLowerCase(), "include");
  }
  for (const term of exclude) {
    kinds.set(term.toLowerCase(), "exclude");
  }
  if (kinds.size === 0 || text === "") {
    return [{ text, kind: null }];
  }
  const pattern = [...kinds.keys()]
    .sort((a, b) => b.length - a.length)
    .map((term) => term.replace(RE_ESCAPE, "\\$&"))
    .join("|");
  const segments: Segment[] = [];
  let last = 0;
  for (const match of text.matchAll(new RegExp(pattern, "gi"))) {
    if (match.index > last) {
      segments.push({ text: text.slice(last, match.index), kind: null });
    }
    segments.push({
      text: match[0],
      kind: kinds.get(match[0].toLowerCase()) ?? null,
    });
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    segments.push({ text: text.slice(last), kind: null });
  }
  return segments;
}

/* --------------------------------------------------------------- summary */

export interface ScreeningSummary {
  total: number;
  screened: number;
  include: number;
  exclude: number;
  maybe: number;
  undecided: number;
  /** Exclusion reasons and how often each was given, most frequent first. */
  reasons: { reason: string; count: number }[];
}

export function summarize(
  recordCount: number,
  verdicts: Verdicts
): ScreeningSummary {
  const counts = { include: 0, exclude: 0, maybe: 0 };
  const reasons = new Map<string, number>();
  for (const verdict of Object.values(verdicts)) {
    counts[verdict.decision] += 1;
    if (verdict.decision === "exclude" && verdict.reason.trim() !== "") {
      reasons.set(verdict.reason, (reasons.get(verdict.reason) ?? 0) + 1);
    }
  }
  const screened = counts.include + counts.exclude + counts.maybe;
  return {
    total: recordCount,
    screened,
    ...counts,
    undecided: recordCount - screened,
    reasons: [...reasons.entries()]
      .map(([reason, count]) => ({ reason, count }))
      .sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason)),
  };
}

/* ------------------------------------------------------------------ export */

/** Stops a spreadsheet reading a text cell as a formula. `unguard` removes it on reload. */
export function csvCell(value: string): string {
  return RE_FORMULA.test(value) ? `'${value}` : value;
}

export const EXPORT_HEADER = [
  "Decision",
  "Reason",
  "Note",
  "Title",
  "Authors",
  "Year",
  "Journal",
  "DOI",
  "Abstract",
  "Keywords",
  "URL",
] as const;

function authorsText(record: RefRecord): string {
  return record.authors
    .map(
      (author) =>
        author.literal ??
        (author.given ? `${author.family}, ${author.given}` : author.family)
    )
    .join("; ");
}

/** Every record with its decision as CSV: the file to keep, and to load again to resume. */
export function exportCsv(
  records: readonly RefRecord[],
  verdicts: Verdicts
): string {
  const rows = records.map((record, index) => {
    const verdict = verdicts[index];
    return [
      verdict?.decision ?? "",
      verdict?.reason ?? "",
      verdict?.note ?? "",
      record.title,
      authorsText(record),
      record.year,
      record.container,
      record.doi,
      record.abstract,
      record.keywords.join("; "),
      record.url,
    ].map(csvCell);
  });
  return `${toDelimited([[...EXPORT_HEADER], ...rows], ",")}\n`;
}

/** The records with the given decisions, in file order. */
export function recordsWith(
  records: readonly RefRecord[],
  verdicts: Verdicts,
  decisions: readonly Decision[]
): RefRecord[] {
  return records.filter((_, index) => {
    const verdict = verdicts[index];
    return verdict !== undefined && decisions.includes(verdict.decision);
  });
}

/* ------------------------------------------------------------- navigation */

export type Filter = "undecided" | "all" | Decision;

export const FILTERS: readonly Filter[] = [
  "undecided",
  "all",
  "include",
  "maybe",
  "exclude",
];

/** Whether a record with this verdict belongs in the view. */
export function inView(filter: Filter, verdict: Verdict | undefined): boolean {
  if (filter === "all") {
    return true;
  }
  if (filter === "undecided") {
    return verdict === undefined;
  }
  return verdict?.decision === filter;
}

/** Indices (in file order) of the records in the view. */
export function visibleIndices(
  recordCount: number,
  verdicts: Verdicts,
  filter: Filter
): number[] {
  const indices: number[] = [];
  for (let index = 0; index < recordCount; index++) {
    if (inView(filter, verdicts[index])) {
      indices.push(index);
    }
  }
  return indices;
}

/** A cursor held inside a view of `size` records (0 when the view is empty). */
export function clampPosition(position: number, size: number): number {
  return Math.min(Math.max(0, position), Math.max(0, size - 1));
}

/**
 * Where the cursor goes after a decision. In the "unscreened" view the decided record
 * leaves the view, so the cursor already points at the next one; elsewhere it steps on.
 */
export function afterDecision(filter: Filter, position: number): number {
  return filter === "undecided" ? position : position + 1;
}
